import type { Dayjs } from 'dayjs';

import type { StoreHours } from '@jane/shared/types';
import { formatTime, timeIsBetween } from '@jane/shared/util';

type DayName =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface StoreScheduleDay {
  closingTime: Dayjs | null;
  dayName: DayName;
  formattedClosingTime: string | null; // hh:mm a
  formattedOpeningTime: string | null; // hh:mm a
  isClosedAllDay: boolean;
  isOpenPartOfTheDay: boolean;
  isTimeDuringDay(time?: Dayjs): boolean;
  openingTime: Dayjs | null;
  willOpenLater(time?: Dayjs): boolean;
}

/**
 * Returns a StoreScheduleDay if the day has no hours defined that day
 */
export function closedScheduleDay(dayName: DayName): StoreScheduleDay {
  return {
    closingTime: null,
    formattedClosingTime: null,
    formattedOpeningTime: null,
    isClosedAllDay: true,
    isOpenPartOfTheDay: false,
    dayName,
    openingTime: null,
    isTimeDuringDay() {
      return false;
    },
    willOpenLater() {
      return false;
    },
  };
}

/**
 * Returns the closing time of the store, or adds a day if the closing time is before the opening time
 */
export const setClosingTime = (
  openingTime: Dayjs,
  closingTime: Dayjs
): Dayjs => {
  if (openingTime.isAfter(closingTime)) {
    return closingTime.clone().add(1, 'day');
  }

  return closingTime;
};

/**
 * Returns a StoreScheduleDay if the day has an opening and closing time defined
 */
export function openScheduleDay({
  closingTime,
  dayName,
  openingTime,
}: {
  closingTime: Dayjs;
  dayName: DayName;
  openingTime: Dayjs;
}): StoreScheduleDay {
  const adjustedClosingTime = setClosingTime(openingTime, closingTime);

  const isTimeDuringDay = (time: Dayjs) => {
    return timeIsBetween(
      time.toDate(),
      openingTime.toDate(),
      adjustedClosingTime.toDate(),
      '[]'
    );
  };

  const willOpenLater = (time: Dayjs) => {
    return openingTime.isAfter(time) && time.isSame(openingTime, 'day');
  };

  return {
    closingTime: adjustedClosingTime,
    dayName,
    formattedClosingTime: formatTime({
      time: closingTime,
      outputFormat: 'h:mm a',
      inputFormat: 'HH:mm',
    }),
    formattedOpeningTime: formatTime({
      time: openingTime,
      outputFormat: 'h:mm a',
      inputFormat: 'HH:mm',
    }),
    isClosedAllDay: false,
    isOpenPartOfTheDay: true,
    openingTime,
    isTimeDuringDay,
    willOpenLater,
  };
}

/**
 * Converts a time string into a DayJs datetime object relative to a starting date
 *
 * This utility function takes a starting day reference point, a number of days to offset,
 * and a time string in "HH:MM" format, and returns a precise DayJs datetime object.
 */
export const getScheduleTime = (
  startingDay: Dayjs,
  daysToAdd: number,
  time: string
): Dayjs | null => {
  const [hour, min] = time.split(':');
  if (!(hour && min)) return null;
  return startingDay
    .clone()
    .add(daysToAdd, 'days')
    .hour(parseInt(hour, 10))
    .minute(parseInt(min, 10))
    .second(0)
    .millisecond(0);
};

/**
 * Creates a StoreScheduleDay object based on opening and closing times
 */
export function buildScheduleDay({
  closingTime,
  dayName,
  openingTime,
}: {
  closingTime: Dayjs | null;
  dayName: DayName;
  openingTime: Dayjs | null;
}): StoreScheduleDay {
  return openingTime && closingTime
    ? openScheduleDay({
        closingTime,
        dayName,
        openingTime,
      })
    : closedScheduleDay(dayName);
}

/**
 * Creates an extended 8-day schedule starting from yesterday
 *
 * This function generates an array of StoreScheduleDay objects covering 8 consecutive days
 * starting from yesterday. The 8-day format (rather than 7) is constructed by duplicating
 * yesterday at the end of the array, ensuring a complete view of the week ahead with context
 * of the previous day.
 *
 * The function:
 * 1. Identifies yesterday's position in the working hours array
 * 2. Reorders the days so that yesterday appears first, followed by the rest of the week
 * 3. Converts each WorkingHour entry into a StoreScheduleDay object with proper date/time values
 * 4. Handles both open days (with time ranges) and closed days (without times)
 *
 * This extended schedule format is useful for operations that need to compare across days
 * or handle overnight operations that span from one day to another.
 * @param params.workingHours - Array of WorkingHour objects from the store configuration
 * @param params.yesterday - DayJs object representing yesterday's date
 * @returns An array of 8 StoreScheduleDay objects starting from yesterday
 */
export function buildExtendedScheduleDays({
  workingHours,
  yesterday,
}: {
  workingHours: StoreHours[];
  yesterday: Dayjs;
}): StoreScheduleDay[] {
  const yesterdayName = yesterday.format('dddd').toLowerCase();
  const yesterdayIndex = workingHours.findIndex(
    (day) => day.day === yesterdayName
  );
  const beforeDays = workingHours.slice(0, yesterdayIndex + 1);
  const restOfWeek = workingHours.slice(yesterdayIndex, workingHours.length);

  return restOfWeek.concat(beforeDays).map((day, index) => {
    const {
      day: name,
      period: { from, to },
    } = day;

    const openingTime = from ? getScheduleTime(yesterday, index, from) : null;
    const closingTime = to ? getScheduleTime(yesterday, index, to) : null;

    return buildScheduleDay({
      closingTime,
      dayName: name as DayName,
      openingTime,
    });
  });
}
